import React, { useCallback, useState } from 'react';
import { FileText, Upload } from 'lucide-react';
import { Button } from '@/shared/ui/button';

interface PDFUploadDropzoneProps {
  onFileSelect: (file: File) => void;
  onFilesSelect?: (files: File[], rejected: File[]) => void;
  isLoading?: boolean;
  title?: string;
  description?: string;
  buttonLabel?: string;
  minHeightClassName?: string;
  multiple?: boolean;
}

const isPdfFile = (file: File): boolean => file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');

export const PDFUploadDropzone: React.FC<PDFUploadDropzoneProps> = ({
  onFileSelect,
  onFilesSelect,
  isLoading = false,
  title = 'Upload a PDF Document',
  description = 'Drag and drop your PDF file here, or click to browse',
  buttonLabel = 'Select PDF File',
  minHeightClassName = 'min-h-[400px]',
  multiple = false,
}) => {
  const [isDragging, setIsDragging] = useState(false);

  const handleSelectedFiles = useCallback(
    (fileList: FileList | null | undefined) => {
      const files = Array.from(fileList || []);
      if (!files.length) return;
      const accepted = files.filter(isPdfFile);
      const rejected = files.filter((file) => !isPdfFile(file));

      if (multiple && onFilesSelect) {
        onFilesSelect(accepted, rejected);
        return;
      }
      if (accepted[0]) {
        onFileSelect(accepted[0]);
      } else {
        alert('Please select a valid PDF file.');
      }
    },
    [multiple, onFileSelect, onFilesSelect]
  );

  const handleFileChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      handleSelectedFiles(event.target.files);
      event.target.value = '';
    },
    [handleSelectedFiles]
  );

  const handleDrop = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      setIsDragging(false);
      handleSelectedFiles(event.dataTransfer.files);
    },
    [handleSelectedFiles]
  );

  const handleDragOver = useCallback((event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
  }, []);

  const handleDragEnter = useCallback((event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(false);
  }, []);

  return (
    <div
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      className={`flex flex-col items-center justify-center w-full h-full ${minHeightClassName} p-8 border-2 border-dashed rounded-lg transition-colors ${
        isDragging
          ? 'border-primary bg-primary/5'
          : 'border-border bg-muted/30 hover:bg-muted/50'
      }`}
    >
      <div className="flex flex-col items-center space-y-4">
        <div className="p-6 bg-primary/10 rounded-full">
          <FileText className="w-12 h-12 text-primary" />
        </div>
        <div className="text-center space-y-2">
          <h3 className="text-xl font-semibold text-foreground">{title}</h3>
          <p className="text-sm text-muted-foreground">{description}</p>
        </div>
        <label htmlFor="pdf-upload-input">
          <Button
            type="button"
            disabled={isLoading}
            className="cursor-pointer"
            onClick={() => document.getElementById('pdf-upload-input')?.click()}
          >
            <Upload className="w-4 h-4 mr-2" />
            {isLoading ? 'Loading...' : buttonLabel}
          </Button>
          <input
            id="pdf-upload-input"
            aria-label={buttonLabel}
            type="file"
            accept=".pdf,application/pdf"
            multiple={multiple}
            onChange={handleFileChange}
            className="hidden"
            disabled={isLoading}
          />
        </label>
        <p className="text-xs text-muted-foreground">Supported format: PDF</p>
      </div>
    </div>
  );
};
